import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { Discipline } from './Discipline';
import { Sportlevel } from './Sportlevel';
import type { EntityState } from './../enums/EntityState';

export class Sport extends BaseObj {
    public ID: number;
    public __id: number;

    public Disciplines: Discipline[];
    public __disciplines: Discipline[];

    public FederationID: number;
    public __federationid: number;

    public Name: string;
    public __name: string;

    public SeasonStart: string | null;
    public __seasonstart: string | null;

    public ShortName: string | null;
    public __shortname: string | null;

    public SportLevels: Sportlevel[];
    public __sportlevels: Sportlevel[];

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Disciplines?: Discipline[];
        EntityState?: EntityState;
        FederationID?: number;
        Name?: string | null;
        SeasonStart?: string | null;
        ShortName?: string | null;
        SportLevels?: Sportlevel[];
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Sport>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Sport tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Disciplines = values.Disciplines ?? [];
        this.__disciplines = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Disciplines ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('Disciplines', newVal),
        });
        this.__disciplines.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Disciplines', this.Disciplines),
            );
        });
        Object.defineProperty(this, 'Disciplines', {
            get(): Discipline[] {
                return this.__disciplines;
            },
            set(value: Discipline[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__disciplines = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: Discipline[], newVal: Discipline[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Disciplines', newVal),
                });
                this.__disciplines.forEach((child: Discipline) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Disciplines', this.Disciplines),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Disciplines', this.Disciplines);
            },
            enumerable: true,
            configurable: true,
        });

        this.FederationID = values.FederationID ?? 0;
        this.__federationid = values.FederationID ?? 0;
        Object.defineProperty(this, 'FederationID', {
            get(): number {
                return this.__federationid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('FederationID', this.__federationid, value);
                this.__federationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SeasonStart = values.SeasonStart ?? '';
        this.__seasonstart = values.SeasonStart ?? '';
        Object.defineProperty(this, 'SeasonStart', {
            get(): string | null {
                return this.__seasonstart;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('SeasonStart', this.__seasonstart, value);
                this.__seasonstart = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShortName = values.ShortName ?? '';
        this.__shortname = values.ShortName ?? '';
        Object.defineProperty(this, 'ShortName', {
            get(): string | null {
                return this.__shortname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ShortName', this.__shortname, value);
                this.__shortname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportLevels = values.SportLevels ?? [];
        this.__sportlevels = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.SportLevels ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('SportLevels', newVal),
        });
        this.__sportlevels.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('SportLevels', this.SportLevels),
            );
        });
        Object.defineProperty(this, 'SportLevels', {
            get(): Sportlevel[] {
                return this.__sportlevels;
            },
            set(value: Sportlevel[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__sportlevels = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: Sportlevel[], newVal: Sportlevel[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('SportLevels', newVal),
                });
                this.__sportlevels.forEach((child: Sportlevel) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('SportLevels', this.SportLevels),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('SportLevels', this.SportLevels);
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Disciplines": [ ${this.Disciplines.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"FederationID": ${JSON.stringify(this.FederationID)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"SeasonStart": ${JSON.stringify(this.SeasonStart)}` +
            ',' +
            `"ShortName": ${JSON.stringify(this.ShortName)}` +
            ',' +
            `"SportLevels": [ ${this.SportLevels.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Sport from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Disciplines?: Discipline[];
        FederationID?: number;
        Name?: string | null;
        SeasonStart?: string | null;
        ShortName?: string | null;
        SportLevels?: Sportlevel[];
        EntityState?: 'Added' | 'Unchanged';
    }): Sport {
        return new Sport(values);
    }

    public static _FromServerJson(json: any): Sport {
        return new Sport({
            ID: json.ID,
            Disciplines: json.Disciplines?.map((x: any) => Discipline._FromServerJson(x)),
            FederationID: json.FederationID,
            Name: json.Name ?? '',
            SeasonStart: json.SeasonStart ?? '',
            ShortName: json.ShortName ?? '',
            SportLevels: json.SportLevels?.map((x: any) => Sportlevel._FromServerJson(x)),
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Sport from another instance of Sport.
     * @param other The Sport you want to clone
     * @returns A cloned Sport
     */
    public static FromOther(other: Sport): Sport {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Sport._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Sport.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'Disciplines' | 'FederationID' | 'Name' | 'SeasonStart' | 'ShortName' | 'SportLevels')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Sport';
}
